import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase, Profile } from '../lib/supabase';
import { sendWelcomeEmail } from '../lib/email-service';
import { isProfileComplete as checkIsProfileComplete } from '../lib/profile-utils';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  profileLoading: boolean;
  isProfileComplete: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// NOTE: We now use the centralized profile-utils.ts for profile completion checks

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    // Set a timeout to prevent infinite loading
    timeoutId = setTimeout(() => {
      console.warn('[AuthContext] Session loading timeout - forcing loading to false');
      setLoading(false);
    }, 5000); // 5 second timeout

    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        clearTimeout(timeoutId);

        if (error) {
          console.error('[AuthContext] Session error:', error);
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        console.log('[AuthContext] Session loaded:', { hasSession: !!session, userId: session?.user?.id });
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          loadProfile(session.user.id);
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('[AuthContext] Unexpected session error:', err);
        clearTimeout(timeoutId);
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      (async () => {
        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        if (event === 'TOKEN_REFRESHED' && !session) {
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          await loadProfile(session.user.id);
        } else {
          setProfile(null);
          setLoading(false);
        }
      })();
    });

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    // Set up real-time subscription for profile changes
    const profileSubscription = supabase
      .channel(`profile-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[AuthContext] Profile change detected:', payload.eventType);
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const updatedProfile = payload.new as Profile;
            console.log('[AuthContext] Profile updated via subscription:', {
              hasJobTitle: !!updatedProfile.job_title,
              hasCompany: !!updatedProfile.company,
              isComplete: checkIsProfileComplete(updatedProfile)
            });
            setProfile(updatedProfile);
          }
        }
      )
      .subscribe();

    return () => {
      profileSubscription.unsubscribe();
    };
  }, [user]);

  const loadProfile = async (userId: string) => {
    setProfileLoading(true);
    console.log('[AuthContext] Loading profile for user:', userId);

    try {
      // Always fetch fresh data from the database
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('[AuthContext] Error loading profile:', error);
        throw error;
      }

      console.log('[AuthContext] Profile loaded successfully:', {
        hasProfile: !!data,
        userId: data?.id,
        email: data?.email,
        hasJobTitle: !!data?.job_title && data.job_title.trim() !== '',
        hasCompany: !!data?.company && data.company.trim() !== '',
        isComplete: checkIsProfileComplete(data)
      });

      // Update state with fresh data
      setProfile(data);
    } catch (error) {
      console.error('[AuthContext] Profile loading failed:', error);
      setProfile(null);
    } finally {
      setLoading(false);
      setProfileLoading(false);
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    console.log('[AuthContext] signUp called:', { email, fullName });

    // FEATURE FLAG: Email verification disabled
    // Users can immediately access the app after signup without verifying email
    // To re-enable: Configure Supabase Auth settings and uncomment verification checks in AuthForm.tsx

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          // FEATURE FLAG: Email verification disabled - no emailRedirectTo needed
          // emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        console.error('[AuthContext] signUp error:', error);
        return { error };
      }

      if (!data.user) {
        const error = new AuthError('User creation failed - no user returned');
        console.error('[AuthContext] signUp failed: No user returned');
        return { error };
      }

      console.log('[AuthContext] User created successfully:', {
        userId: data.user.id,
        email: data.user.email
      });

      // CRITICAL: Set session and user immediately to trigger navigation
      setSession(data.session);
      setUser(data.user);

      // The database trigger (handle_new_user) automatically creates the profile
      // Wait a moment for the trigger to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Load the profile to trigger dashboard redirect
      await loadProfile(data.user.id);

      // Verify profile was created
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user.id)
        .maybeSingle();

      if (profileError) {
        console.error('[AuthContext] Profile verification error:', profileError);
      } else if (!profileData) {
        console.warn('[AuthContext] Profile not found after signup - may need manual creation');
      } else {
        console.log('[AuthContext] Profile verified successfully:', {
          userId: profileData.id,
          email: profileData.email,
          hasFullName: !!profileData.full_name
        });
      }

      // Send welcome email asynchronously (don't block on failure)
      sendWelcomeEmail({
        recipientName: fullName,
        recipientEmail: email
      }).catch(err => {
        console.warn('[AuthContext] Failed to send welcome email:', err);
      });

      return { error: null };
    } catch (error) {
      console.error('[AuthContext] signUp exception:', error);
      return { error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    console.log('[AuthContext] signIn called:', { email });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('[AuthContext] signIn error:', error);
        return { error };
      }

      if (!data.user) {
        const error = new AuthError('Sign in failed - no user returned');
        console.error('[AuthContext] signIn failed: No user returned');
        return { error };
      }

      console.log('[AuthContext] User signed in successfully:', {
        userId: data.user.id,
        email: data.user.email
      });

      return { error: null };
    } catch (error) {
      console.error('[AuthContext] signIn exception:', error);
      return { error: error as AuthError };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setSession(null);
      setUser(null);
      setProfile(null);
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    // Get current session to determine user ID
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    const userId = user?.id || currentSession?.user?.id;

    if (!userId) {
      const error = new Error('Unable to determine user ID. Please try logging in again.');
      console.error('[AuthContext] updateProfile failed: No user ID available');
      return { error };
    }

    console.log('[AuthContext] updateProfile called with:', {
      userId,
      updates: Object.keys(updates),
      hasUserObject: !!user,
      hasSession: !!currentSession,
      timestamp: new Date().toISOString()
    });

    try {
      // CRITICAL FIX: Use maybeSingle() instead of single() to prevent JSON coercion errors
      // maybeSingle() safely handles 0 or 1 rows, while single() throws on 0 rows
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .maybeSingle();

      if (error) {
        console.error('[AuthContext] updateProfile database error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      if (!data) {
        // Fallback: Use the safe upsert function for new users
        console.warn('[AuthContext] Standard update returned no data, trying fallback upsert function');

        try {
          const { data: upsertData, error: upsertError } = await supabase
            .rpc('upsert_user_profile', {
              p_job_title: updates.job_title || null,
              p_company: updates.company || null,
              p_bio: updates.bio || null,
            })
            .maybeSingle();

          if (upsertError) {
            console.error('[AuthContext] Upsert fallback failed:', upsertError);
            throw upsertError;
          }

          if (!upsertData) {
            const error = new Error('Profile update failed - unable to create or update profile. Please try again or contact support.');
            console.error('[AuthContext] Both update and upsert failed', {
              userId,
              updates
            });
            throw error;
          }

          console.log('[AuthContext] Profile upserted successfully via fallback:', {
            hasJobTitle: !!upsertData.job_title,
            hasCompany: !!upsertData.company,
            isComplete: checkIsProfileComplete(upsertData)
          });

          // Update local state with fresh data
          setProfile(upsertData as Profile);

          // Force a profile reload to ensure UI is in sync
          await loadProfile(userId);

          return { error: null };
        } catch (fallbackError) {
          console.error('[AuthContext] Fallback upsert exception:', fallbackError);
          throw fallbackError;
        }
      }

      console.log('[AuthContext] Profile updated successfully:', {
        hasJobTitle: !!data.job_title,
        hasCompany: !!data.company,
        isComplete: checkIsProfileComplete(data)
      });

      // Update local state with fresh data
      setProfile(data as Profile);

      // Force a profile reload to ensure UI is in sync
      await loadProfile(userId);

      return { error: null };
    } catch (error) {
      console.error('[AuthContext] updateProfile exception:', error);
      return { error: error as Error };
    }
  };

  // Use centralized profile completion check
  const isProfileComplete = checkIsProfileComplete(profile);

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        loading,
        profileLoading,
        isProfileComplete,
        signUp,
        signIn,
        signOut,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
